/** Workspace, member, invitation and settings payload schemas. */

import { z } from "zod";
import { PLAN_PRICING } from "../constants/plans";
import { INVOICE_PAGE_SIZE_IDS } from "../constants/invoice";
import { ASSIGNABLE_WORKSPACE_ROLE_VALUES, MEMBER_STATUSES } from "../constants/roles";
import { WORKSPACE_DEFAULTS, INVITATION_RULES } from "../constants/workspaces";
import { emailSchema, nameSchema, pageQuerySchema, phoneSchema, slugSchema } from "./common";

export const workspacePlanSchema = z.enum(["free", "pro"]);

export const createWorkspaceSchema = z.object({
  name: nameSchema,
  slug: slugSchema,
  plan: workspacePlanSchema.default("free"),
});

export const updateWorkspaceSchema = z
  .object({
    name: nameSchema.optional(),
    archived: z.boolean().optional(),
  })
  .refine((value) => value.name !== undefined || value.archived !== undefined, {
    message: "Nothing to update.",
  });

export const workspaceSettingsSchema = z.object({
  invoice_prefix: z.string().trim().min(1).max(12).default(WORKSPACE_DEFAULTS.invoicePrefix),
  invoice_page_size: z
    .enum(INVOICE_PAGE_SIZE_IDS)
    .default(WORKSPACE_DEFAULTS.invoicePageSize),
  currency: z.string().trim().length(3).default(WORKSPACE_DEFAULTS.currency),
  tax_percent: z.coerce.number().min(0).max(100).default(WORKSPACE_DEFAULTS.taxPercent),
  address: z.string().trim().max(300).nullish().default(null),
  phone: phoneSchema.nullish().default(null),
  whatsapp_number: phoneSchema.nullish().default(null),
  timezone: z.string().trim().min(1).max(64).default(WORKSPACE_DEFAULTS.timezone),
});

/**
 * Deliberately declared field-by-field without defaults: a partial update must
 * never silently re-apply creation defaults over stored settings.
 */
export const updateWorkspaceSettingsSchema = z
  .object({
    invoice_prefix: z.string().trim().min(1).max(12).optional(),
    invoice_page_size: z.enum(INVOICE_PAGE_SIZE_IDS).optional(),
    currency: z.string().trim().length(3).optional(),
    tax_percent: z.coerce.number().min(0).max(100).optional(),
    address: z.string().trim().max(300).nullish(),
    phone: phoneSchema.nullish(),
    whatsapp_number: phoneSchema.nullish(),
    timezone: z.string().trim().min(1).max(64).optional(),
  })
  .refine((value) => Object.keys(value).length > 0, { message: "Nothing to update." });

export const inviteMemberSchema = z.object({
  email: emailSchema,
  role: z.enum(ASSIGNABLE_WORKSPACE_ROLE_VALUES),
});

export const updateMemberRoleSchema = z.object({
  role: z.enum(ASSIGNABLE_WORKSPACE_ROLE_VALUES),
});

export const updateMemberStatusSchema = z.object({
  status: z.enum([MEMBER_STATUSES.active, MEMBER_STATUSES.suspended]),
});

export const acceptInvitationSchema = z.object({
  token: z.string().trim().length(INVITATION_RULES.tokenBytes * 2),
});

export const workspaceListQuerySchema = pageQuerySchema.extend({
  q: z.string().trim().max(120).optional(),
});

export const planPricingSchema = z.record(
  z.string(),
  z.record(z.string(), z.object({ amount: z.number(), currency: z.string() })),
);

export const PLAN_PRICE_KEYS = Object.keys(PLAN_PRICING) as readonly string[];
